import { findLastIndex } from 'lodash';
import selectors from '../rootSelectors';
import DNOServiceClient from '../common/clients/DNOServiceClient';
import { DAZE_FETCH_RETRY } from '../common/api/apiRetryGetConstants';
import { getStudentResults, saveMeasureResponse } from '../common/api/dnoActions';
import { COMPREHENSION, DAZE, RESULT_RESPONSE_OUTCOME } from '../constants/measure';
import { setPlaylistItemCompleted } from '../common/playlist/playlistActions';
import { clearForm } from '../common/api/formsActions';

const measure = DNOServiceClient.CLIENT_TO_SERVER_MEASURE_NAME[DAZE];
const measureType = COMPREHENSION;

export const SET_DAZE_CONTENT = 'SET_DAZE_CONTENT';
export const SELECT_DAZE_RESPONSE = 'SELECT_DAZE_RESPONSE';
export const SET_IS_READY_SCRIPT_PLAYED = 'SET_IS_READY_SCRIPT_PLAYED';
export const CLEAR_DAZE_RESPONSES = 'CLEAR_DAZE_RESPONSES';
export const MADE_FIRST_ATTEMPT = 'MADE_FIRST_ATTEMPT';
export const HIGHLIGHT_DAZE_RESPONSE = 'HIGHLIGHT_DAZE_RESPONSE';
export const HIGHLIGHT_DAZE_RESPONSES = 'HIGHLIGHT_DAZE_RESPONSES';
export const DEHIGHLIGHT_DAZE_RESPONSES = 'DEHIGHLIGHT_DAZE_RESPONSES';
export const SET_RESPONSES_STRUCTURE = 'SET_RESPONSES_STRUCTURE';
export const SET_DAZE_SCREEN_MODE = 'SET_DAZE_SCREEN_MODE';

export const setDazeContent = (content) => ({
    type: SET_DAZE_CONTENT,
    content,
});

export const setDazeScreenMode = (screenMode) => ({
    type: SET_DAZE_SCREEN_MODE,
    screenMode,
});

export const selectResponse = (questionIndex, responseIndex) => ({
    type: SELECT_DAZE_RESPONSE,
    questionIndex,
    responseIndex,
});

export const clearResponses = () => ({
    type: CLEAR_DAZE_RESPONSES,
});

export const highlightDazeResponse = (questionIndex, responseIndex) => ({
    type: HIGHLIGHT_DAZE_RESPONSE,
    questionIndex,
    responseIndex,
});

export const highlightDazeResponses = (responses) => ({
    type: HIGHLIGHT_DAZE_RESPONSES,
    responses,
});

export const dehighlightDazeResponses = () => ({
    type: DEHIGHLIGHT_DAZE_RESPONSES,
});

export const setIsReadyScriptPlayed = (isPlayed) => ({
    type: SET_IS_READY_SCRIPT_PLAYED,
    isPlayed,
});

export const madeFirstAttempt = () => ({
    type: MADE_FIRST_ATTEMPT,
});

export const createResponsesStructure = (responsesItems) => ({
    type: SET_RESPONSES_STRUCTURE,
    responsesItems,
});

export const fetchDazeContent = () => (dispatch, getStore) => {
    const state = getStore();
    const form = selectors.findNextForm(state, measure, measureType);
    if (!form) {
        // NOTE: case when all PM forms are done and user navigates to assessment by url
        // we expect this never to be a real use case
        return Promise.resolve();
    }
    return dispatch(DNOServiceClient.getMeasureForm(
        DAZE, measureType, form.id, DAZE_FETCH_RETRY,
    ))
        .then((contentJson) => {
            if (contentJson) {
                dispatch(setDazeContent(contentJson));
            }
        });
};

/**
 * Transform user selected responses to server desirable format.
 *
 * { questionId1: responseId1, ...} will be transformed to:
 *
 * {
 *      form_id: @formId,
 *      responses: [
 *          {  item_id: questionId1, response_id: responseId1, [outcome: SK | NA]}
 *      ]
 * }
 * Outcomes are populated for questions with null response:
 * - SK if the question was skipped
 * - NA if the question was not answered b/c of timeout
 *
 * @param  {String}  formId          Daze form id
 * @param  {Object}  response        user selected responses
 * @param  {Boolean} isDiscontinued  whether Daze result was discontinued by timeout
 * @return {Object}                  prepared Daze response object
 */
const formatDazeResponses = (formId, responses, isDiscontinued) => {
    // map of selected responses -> list of response objects
    const resultsList = [];
    Object.keys(responses).forEach((itemId) => {
        resultsList.push({ item_id: itemId, response_id: responses[itemId] });
    });
    const lastAttemptedResponseIndex = findLastIndex(resultsList, (item) => item.response_id != null);

    // add outcomes for not attempted responses
    const resultsListWithOutcomes = resultsList.map((item, index) => {
        if (item.response_id === null) {
            if (index < lastAttemptedResponseIndex) {
                return { ...item, outcome: RESULT_RESPONSE_OUTCOME.SKIPPED };
            } if (isDiscontinued) {
                return { ...item, outcome: RESULT_RESPONSE_OUTCOME.NOT_ATTEMPTED };
            }
            return { ...item, outcome: RESULT_RESPONSE_OUTCOME.NOT_ATTEMPTED };
        }
        return item;
    });

    return {
        responses: resultsListWithOutcomes,
        form_id: formId,
    };
};

export const saveDazeResponse = (isDiscontinued, isRetry) => (dispatch, getStore) => {
    const state = getStore();
    const formId = selectors.getDazeFormId(state);
    const isComplete = true;
    const selectedResponses = selectors.getDazeSelectedResponses(state);
    const resultResponse = formatDazeResponses(
        formId,
        selectedResponses,
        isDiscontinued,
    );
    const getResultsPromise = isRetry ? dispatch(getStudentResults(DAZE))
        : Promise.resolve();

    return getResultsPromise.then(() => {
        const result = selectors.findResultFor(getStore(), { measure, measureType, formId });

        if (isRetry && result && result.isComplete) {
            return Promise.resolve();
        }

        return dispatch(saveMeasureResponse(measure, measureType, formId, resultResponse,
            isComplete));
    });
};

/**
 * Populates initial values for Daze selected responses based on questions ids
 * from content in the next format:
 *    {
 *        questionId1: null,
 *        questionId2: null,
 *        ...
 *        questionIdN: null,
 *    }
 */
export const initResponsesStructure = () => (dispatch, getStore) => {
    const state = getStore();
    const questionItems = selectors.getDazeQuestionItems(state);
    const responsesItems = questionItems.reduce((result, item) => ({ ...result, [item.id]: null }), {});
    dispatch(createResponsesStructure(responsesItems));
};

export const completeDazeAssessment = () => (dispatch, getStore) => {
    const state = getStore();
    dispatch(clearForm(selectors.getDazeFormId(state)));
    dispatch(setPlaylistItemCompleted(DAZE));
};
